import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';

import history from 'browserHistory';
import App from './App';
import configureStore from './store';
import reportWebVitals from './reportWebVitals';
import { msalConfig } from './modules/auth/containers/Auth/constants';

import './index.css';
import './custom.scss';

const authIndexLog = (message: string, details?: unknown) => {
  const isDebugEnabled = process.env.NODE_ENV !== 'production' || localStorage.getItem('app_auth_debug') === '1';

  if (!isDebugEnabled) {
    return;
  }

  // eslint-disable-next-line no-console
  console.log(`[AUTH][index] ${message}`, details ?? '');
};

const store = configureStore();
// @ts-ignore
export const publicClientApplication = new PublicClientApplication(msalConfig);

publicClientApplication.initialize();
const accounts = publicClientApplication.getAllAccounts();
authIndexLog('MSAL initialized', { accountsCount: accounts.length });

if (accounts.length > 0) {
  publicClientApplication.setActiveAccount(accounts[0]);
  authIndexLog('Active account set from cache', { username: accounts[0].username });
}

publicClientApplication.addEventCallback((message: EventMessage) => {
  authIndexLog('MSAL event', {
    eventType: message.eventType,
    hasPayload: Boolean(message.payload),
    hasError: Boolean(message.error),
  });

  if (message.eventType === EventType.LOGOUT_SUCCESS) {
    localStorage.removeItem('role');
    localStorage.removeItem('depTitle');
    publicClientApplication.setActiveAccount(null);
    authIndexLog('LOGOUT_SUCCESS handled');
  }
  if (message.eventType === EventType.LOGIN_SUCCESS) {
    const payload = message.payload as AuthenticationResult;
    if (payload?.account) {
      publicClientApplication.setActiveAccount(payload.account);
      authIndexLog('LOGIN_SUCCESS account set', { username: payload.account.username });
    }
    sessionStorage.removeItem('app_logout_in_progress');
    sessionStorage.removeItem('app_auth_invalidate_count');
    authIndexLog('Auth session flags cleared after login success');
  }
  if (message.eventType === EventType.LOGIN_FAILURE || message.eventType === EventType.LOGOUT_FAILURE) {
    sessionStorage.removeItem('app_logout_in_progress');
    sessionStorage.removeItem('app_auth_invalidate_count');
    localStorage.clear();
    authIndexLog('LOGIN/LOGOUT failure handled, redirecting to /not-found', {
      error: message.error,
    });
    history.push('/not-found', { error: message.error });
  }
});

ReactDOM.render(
  <MsalProvider instance={publicClientApplication}>
    <Provider store={store}>
      <App />
    </Provider>
  </MsalProvider>,
  document.getElementById('root'),
);

reportWebVitals();
